/**
 * @file 游戏列表行左侧信息区（Layer 1 纯 UI）。
 * 由 `GameRow` 渲染，展示 logo、主副标题与平台描述。
 */
import React, { useMemo } from 'react';
import classNames from 'classnames';
import defaultLogo from '@/pages/dashboard/assets/img/svg/gameError.svg';
import DefaultIcon from '@/pages/dashboard/assets/svg/console-game-default.svg';
import SvgIcon from '@/pages/home/components/SvgIcon';
import TextBubbles from '@home/components/TextBubbles';
import HighlightText from '@/pages/aigc/components/HighlightText';
import { CHART_GAME_LIST_TEXT_BUBBLE_PROPS } from '../bubbleOverlay';
import type { IGameInfoProps } from '../types';

/** 平台 logo 配置项 */
interface ILogoMapItem {
  className: string;
  defaultLogo: string;
}

/** 平台类型与默认 logo 映射 */
const LOGO_MAP: Record<string, ILogoMapItem> = {
  mobile: { className: 'dba-new-chart-game-row__logo--mobile', defaultLogo: DefaultIcon },
  pc: { className: 'dba-new-chart-game-row__logo--pc', defaultLogo: DefaultIcon },
  console: { className: 'dba-new-chart-game-row__logo--console', defaultLogo: DefaultIcon },
};

/**
 * 从 `game_type` 取首个合法平台 key。
 *
 * @param gameType - 逗号分隔的平台类型字符串
 * @returns `mobile` | `pc` | `console`，非法值回退 `mobile`
 */
const getGameTypeKey = (gameType?: string): string => {
  const first = gameType?.split(',')[0];
  return ['mobile', 'pc', 'console'].includes(first || '') ? first! : 'mobile';
};

/**
 * 游戏行左侧信息区：logo、名称、平台描述。
 *
 * @param props - 展示属性，见 {@link IGameInfoProps}
 *
 * @remarks
 * - 主标题 / 副标题溢出 tips 对齐 Skill 列表，使用 `@home/components/TextBubbles`
 * - 副标题行含平台 icon + 竖线分隔符 + 描述文案（对齐 Figma）
 */
const GameInfo: React.FC<IGameInfoProps> = ({
  gameInfo,
  isMajorVersion = false,
  searchValue = '',
  showArrow = false,
}) => {
  const {
    cover,
    logo,
    game_name: gameNameEN,
    game_cn_name: gameNameCN,
    game_type: gameType,
  } = gameInfo ?? {};

  const name = gameNameEN || gameNameCN || '';
  const desc = gameNameCN || gameNameEN || '';
  const typeKey = getGameTypeKey(gameType);
  const logoConfig = LOGO_MAP[typeKey];
  const showSearchHighlight = isMajorVersion && !!searchValue;

  const gameTypeIcon = useMemo(() => {
    if (gameType?.includes('mobile') && !gameType?.includes('pc')) {
      return 'other-icon';
    }
    if (gameType?.includes('pc')) {
      return 'monitor';
    }
    if (gameType === 'console') {
      return 'merit';
    }
    return 'monitor';
  }, [gameType]);

  const titleText = showSearchHighlight
    ? <HighlightText name={name} keyword={searchValue} />
    : name;

  return (
    <div className="dba-new-chart-game-row__context">
      <div className="dba-new-chart-game-row__left">
        <div className="dba-new-chart-game-row__cover">
          <div
            className={classNames(
              'dba-new-chart-game-row__logo',
              logoConfig?.className,
            )}
          >
            <img src={cover || logo || logoConfig?.defaultLogo || defaultLogo} alt="" />
          </div>
        </div>
        <div className="dba-new-chart-game-row__text-wrap">
          <div className="dba-new-chart-game-row__title-line">
            <TextBubbles
              text={titleText}
              tipContent={name}
              rowNum={1}
              placement="top"
              className="dba-new-chart-game-row__title"
              bubbleProps={CHART_GAME_LIST_TEXT_BUBBLE_PROPS}
            />
          </div>
          <div className="dba-new-chart-game-row__desc">
            {gameType !== 'none' ? (
              <>
                <SvgIcon
                  className="dba-new-chart-game-row__desc-icon"
                  type={gameTypeIcon}
                />
                <span
                  className="dba-new-chart-game-row__desc-divider"
                  aria-hidden
                />
              </>
            ) : null}
            <TextBubbles
              text={desc}
              rowNum={1}
              placement="top"
              className="dba-new-chart-game-row__desc-text"
              bubbleProps={CHART_GAME_LIST_TEXT_BUBBLE_PROPS}
            />
          </div>
        </div>
      </div>
      {showArrow ? (
        <SvgIcon type="point-right" className="dba-new-chart-game-row__arrow" />
      ) : null}
    </div>
  );
};

export default GameInfo;
